import { HEADER_SEARCH_SUGGESTION_ID, Suggestions } from './Suggestions';
import {
	handleSubmit,
	refreshSuggestions,
	searchCategorySeoName,
	searchQueryFromSuggest,
	suggestsData,
} from './signals';
import { Signal } from '@preact/signals';
import { filters } from 'signals/filters';
import { Icon } from 'components/atoms/Icon.tsx';
import debounce from 'lodash/debounce';
import { useEffect, useRef } from 'preact/hooks';
import { useSignal } from '@preact/signals';
import { localize } from 'i18n/localize';
import { cn } from 'utils/cn';
import { useFocusTrap } from 'utils/focusTrap';
import { FilterLocation } from 'components/filters/FilterLocation.tsx';
import { DEFAULT_RADIUS_VALUE, LOCALITY_CELA_CR_AS_SUGGESTION } from 'utils/constants.ts';
import { GET_GPS_LOCATION_ID } from 'components/filters/location/SuggestionsUtils.tsx';
import { getCurrentPositionLocality } from 'lib/geo/geo.ts';
import { getDefaultRadius } from 'utils/locality.ts';
import { CATEGORY_ALL_CATEGORIES } from 'service/category/model.ts';
import { localityTypeToAnalyticsLocalityType, sendFilterAnalytics } from 'lib/dot/filter/utils.ts';

const $t = localize();

export function InputBox({
	className,
	includeLocationFilter = false,
	isFromHeader,
	isSearchBoxFocused,
	offersCount,
	placeholder,
	searchBlockId,
	searchFieldId,
	searchLocationId,
	showSuggests,
	onLocalityFocusIn,
	onLocalityFocusOut,
}: {
	className?: string;
	includeLocationFilter: boolean;
	isFromHeader?: boolean;
	isSearchBoxFocused: Signal<boolean>;
	offersCount?: number;
	placeholder?: string;
	searchBlockId: string;
	searchFieldId: string;
	searchLocationId: string;
	showSuggests: Signal<boolean>;
	onLocalityFocusIn: () => void;
	onLocalityFocusOut: () => void;
}) {
	const debouncedRefreshSuggestions = debounce((event: Event) => {
		refreshSuggestions((event.target as HTMLInputElement).value, showSuggests);
	}, 300);
	const focusedIndex = useSignal(-1);
	useEffect(() => {
		focusedIndex.value = -1;
	}, [suggestsData.value]);

	const [firstElementRef, lastElementRef, containerElementRef] = useFocusTrap();
	const isExpanded = suggestsData.value.length > 0 && showSuggests.value;
	const keyboardControlsAllowed = isExpanded && isSearchBoxFocused.value;

	useEffect(() => {
		if (isExpanded) {
			addEventListener('keydown', handleKeyDown);
		} else {
			removeEventListener('keydown', handleKeyDown);
		}

		return () => {
			removeEventListener('keydown', handleKeyDown);
		};
	}, [isExpanded]);

	const handleKeyDown = (event: KeyboardEvent) => {
		if (!keyboardControlsAllowed) return;

		if (event.code === 'Escape') {
			event.preventDefault();
			showSuggests.value = false;
			isSearchBoxFocused.value = false;
			focusedIndex.value = -1;
		} else if (event.code === 'ArrowUp') {
			event.preventDefault();
			focusedIndex.value = focusedIndex.value - 1;
			if (focusedIndex.value < 0) {
				focusedIndex.value = suggestsData.value.length - 1;
			}
		} else if (event.code === 'ArrowDown') {
			event.preventDefault();
			focusedIndex.value = (focusedIndex.value + 1) % suggestsData.value.length;
		} else if (focusedIndex.value >= 0 && event.key === 'Enter') {
			// To not copy the logic of the suggestion click, we simulate the click on the suggestion
			const suggestions = globalThis.document.getElementById(`header-suggestions`);
			if (suggestions) {
				event.preventDefault();
				(
					suggestions
						.querySelectorAll(`#header-suggestions .suggestion`)
						.item(focusedIndex.value) as HTMLDivElement
				)?.click();
			}
		} else if (event.key === 'Home') {
			event.preventDefault();
			focusedIndex.value = 0;
		} else if (event.key === 'End') {
			event.preventDefault();
			focusedIndex.value = suggestsData.value.length - 1;
		}
	};

	// We need same padding for input and h1 element
	const inputPadding = cn('pl-6 p-4', {
		'px-5 pr-[5.5rem] py-[10px] leading-5 md:px-5 md:pr-[6.5rem] md:py-[13px]': isFromHeader,
	});
	const inputRef = useRef<HTMLInputElement>(null);
	const inputElement = (
		<input
			data-test="filter-query"
			id={searchFieldId}
			className={cn(
				'w-full border rounded-full font-medium focus:border-tyrkys-100 focus:outline-none [&::-webkit-search-cancel-button]:hidden border-gray-input basis-1/2 flex-grow placeholder:font-medium placeholder:text-dark-blue-50 speakable searchquery',
				includeLocationFilter && 'rounded-r-full md:rounded-r-none',
				inputPadding,
				isSearchBoxFocused.value && !isFromHeader && 'py-2 md:py-4',
			)}
			type="search"
			name={searchFieldId}
			autoComplete="off"
			placeholder={placeholder ?? $t.searchBox.searchText}
			ref={inputRef}
			value={filters.value?.hledej?.seoName ?? ''}
			aria-autocomplete="list"
			aria-activedescendant={
				focusedIndex.value >= 0 ? `${HEADER_SEARCH_SUGGESTION_ID}-${focusedIndex.value}` : undefined
			}
			aria-controls="header-suggestions"
			aria-expanded={isExpanded}
			aria-haspopup="listbox"
			aria-label={$t.searchBox.searchAction}
			aria-owns="header-suggestions"
			autocomplete="off"
			onInput={(event) => {
				filters.value.hledej.seoName = (event.target as HTMLInputElement)?.value ?? '';

				// If we input our own text, do not associate any category with it
				searchCategorySeoName.value = CATEGORY_ALL_CATEGORIES;
				searchQueryFromSuggest.value = false;

				debouncedRefreshSuggestions(event);
			}}
			onFocus={(event) => {
				isSearchBoxFocused.value = true;
				showSuggests.value = true;
				refreshSuggestions((event.target as HTMLInputElement).value, showSuggests);
			}}
			onBlur={(event) => {
				// showSuggests is handled by click-away, however onBlur is needs to catch tab-away...
				// However, when clicking on a suggestion, the blur happens before the click event. It then hides the
				// suggestions before the click registers, making clicking suggestions impossible. Use the CSS class
				// to determine if the blur was caused by clicking a suggestion, and if so, do not hide them. They will
				// be hidden by the click event in different event handler.
				if (!(event.relatedTarget as HTMLElement)?.classList.contains('suggestion')) {
					showSuggests.value = false;
				}
			}}
		/>
	);

	const loadingGps = useSignal(false);
	const radiusDropdownContainerRef = useRef<HTMLDivElement>(null);
	const focusLocationFilter = useSignal(false);

	const localityFilter = (
		<FilterLocation
			radiusValue={
				filters.value.radius?.seoName === undefined
					? DEFAULT_RADIUS_VALUE
					: Number(filters.value.radius.seoName)
			}
			localityValue={filters.value.locality}
			onRadiusInput={(_event, value, source) => {
				const isSourceUser = source === 'user';

				filters.updateRadius(value, isSourceUser);
			}}
			onEnterPassThrough={() => {
				handleSubmit(new Event('submit'), isSearchBoxFocused, showSuggests, searchQueryFromSuggest.peek());
			}}
			loadingGps={loadingGps.value}
			onLocationInput={async (_event, suggestion, resolveGpsLocalityCallback) => {
				_event.preventDefault();
				if (suggestion && suggestion.entityId === GET_GPS_LOCATION_ID) {
					const locality = await getCurrentPositionLocality(loadingGps);
					onLocalityFocusOut?.();
					focusLocationFilter.value = false;
					if (locality) {
						const radius = getDefaultRadius();
						resolveGpsLocalityCallback(locality, radius);
						filters.updateLocality(locality, true);
						filters.updateRadius(radius, false);
					}
				} else {
					onLocalityFocusOut?.();
					focusLocationFilter.value = false;
					if (suggestion) {
						filters.updateLocalityFromSuggest(suggestion);
						sendFilterAnalytics({
							filterName: 'locality',
							filterType: 'value',
							actionType: 'select',
							filterValue: suggestion.suggestFirstRow,
							localityType: localityTypeToAnalyticsLocalityType(suggestion.entityType),
							nearby: 0,
						});
					} else {
						filters.updateLocalityFromSuggest(LOCALITY_CELA_CR_AS_SUGGESTION, true);
					}
				}
			}}
			wrapperClassname="h-full flex-grow"
			inputClass={cn(
				'border border-gray-input md:border-0 focus:border-tyrkys-100 focus:outline-none py-4 font-medium md:text-base',
				isSearchBoxFocused.value && !isFromHeader && 'py-2 md:py-4',
			)}
			dynamicBackground={false}
			useLocalState={true}
			speakable={true}
			suggestionContainerClass="md:w-[calc(100%_+_64px)]"
			radiusDropdownContainerPortalRefElement={radiusDropdownContainerRef}
			focusInputOnSuggestionClick={false}
		/>
	);

	return (
		<div class={cn('relative', className)} id={searchBlockId} ref={containerElementRef}>
			<div class="relative">
				{showSuggests.value && (
					<span title={$t.suggest.windowStartAnchorTitle} ref={firstElementRef} tabIndex={0} />
				)}
				<div class="relative flex [&>.query-wrapper>input]:md:focus-within:border-r-tyrkys-100 flex-col md:flex-row">
					<div
						class={cn(
							'query-wrapper w-full h-full basis-1/2 flex-grow relative',
							focusLocationFilter.value && 'hidden md:block',
						)}
					>
						{inputElement}
						<button
							type="button"
							className={cn(
								'absolute top-1/2 -translate-y-1/2 cursor-pointer right-[5.5rem] md:right-4 hidden',
								isFromHeader && 'right-[3.7rem] md:right-[4.5rem]',
								!isFromHeader && isSearchBoxFocused.value && 'right-4 md:right-4',
								filters.value?.hledej?.seoName && 'block',
							)}
							onClick={() => {
								if (filters.peek().hledej) {
									filters.value.hledej.seoName = '';
									if (inputRef.current) {
										inputRef.current.value = '';
										inputRef.current.focus();
									}
								}
							}}
							title={$t.filters.clearLocality}
						>
							<Icon
								type="close"
								className="block text-dark-blue-25 hover:text-dark-blue-50 focus:text-dark-blue-50 font-light"
								size={22}
							/>
						</button>
					</div>
					{includeLocationFilter && (
						<div
							class={cn(
								'flex basis-1/2 md:border border-gray-input border-solid rounded-r-full md:border-l-0 focus-within:border-tyrkys-100 focus-within:outline-none mt-3 md:mt-0',
								!isSearchBoxFocused.value && 'hidden md:flex',
								isSearchBoxFocused.value &&
									showSuggests.value &&
									suggestsData.value.length > 0 &&
									'hidden md:flex',
							)}
							id={searchLocationId}
							onFocusIn={(event) => {
								if ((event.target as HTMLElement).getAttribute('data-element-type') === 'query') {
									focusLocationFilter.value = true;
									onLocalityFocusIn?.();
								}
							}}
							onFocusOut={(event) => {
								if (
									event.currentTarget.contains(event.relatedTarget as Node) ||
									radiusDropdownContainerRef.current?.contains(event.relatedTarget as Node)
								) {
									return;
								}
								onLocalityFocusOut?.();
								focusLocationFilter.value = false;
							}}
						>
							{localityFilter}
							{/* use dummy div to narrow the locality filter above to create space for search button */}
							<div class="md:w-16"></div>
						</div>
					)}
					<div ref={radiusDropdownContainerRef}></div>
				</div>
				<button
					type="submit"
					title={$t.searchBox.searchAction}
					data-dot="lista_hledani_button"
					className={cn(
						'flex-none flex bg-red-100 hover:bg-red-hover rounded-full items-center absolute top-1/2 right-1.5 md:right-1 -translate-y-1/2',
						isFromHeader ? 'py-[6px] px-[14px] md:px-5 md:py-2.5' : 'py-3 px-6',
						isSearchBoxFocused.value && !isFromHeader && 'hidden md:flex',
					)}
				>
					<Icon type="search" size={isFromHeader ? 20 : 24} className="text-white font-medium" />
				</button>
			</div>
			<div
				class={cn({
					'md:hidden': !showSuggests.value,
					hidden: !isSearchBoxFocused.value || !showSuggests.value,
				})}
			>
				<Suggestions
					className={offersCount ? 'lg:w-full' : ''}
					focusedIndex={focusedIndex.value}
					isFromHeader={isFromHeader}
					isSearchBoxFocused={isSearchBoxFocused}
					searchBlockId={searchBlockId}
					searchLocationId={searchLocationId}
					showSuggests={showSuggests}
				/>
			</div>
			<span title={$t.suggest.windowEndAnchorTitle} ref={lastElementRef} tabIndex={0} />
		</div>
	);
}
